/**
 * WorkItemBoard — Control Tower Kanban Board.
 *
 * Column layout with drag-and-drop via @dnd-kit/core.
 * Receives the same filtered `items` array as the timeline view.
 *
 * Features:
 *   - 7 columns: Backlog, Ready, In Progress, Done, QC, Released (7-day), Exceptions
 *   - Optimistic status updates with revert-on-error
 *   - Drop validation against WORK_ITEM_TRANSITIONS client mirror
 *   - MoveToExceptionsDialog for blocked/escalated moves
 *   - WIP limit column-level indicator (LEAN-01)
 *   - Released column 7-day window with "Show all" toggle
 *   - Exceptions column folds Blocked + Escalated
 *   - Only root items rendered (parentWorkItemId == null)
 *   - Source card 40% opacity during drag; DragOverlay clone follows pointer
 *   - prefers-reduced-motion: drop animation disabled
 *
 * Fix-wave 1.1.3:
 *   - Fix 2: Trello-style fixed height — board container never grows past the viewport;
 *     horizontal scrollbar always at the bottom of the board container
 *   - Fix 7: Column-level sort via `boardSort` prop — default, priority, due-asc, title, updated
 *
 * v1.1.4:
 *   - Board container now uses h-full (replacing the old calc-based approach).
 *     The fullHeight route mode + RorosPage fullHeight + flex-column in ControlTowerPage
 *     propagate a concrete height so h-full resolves to the available viewport height.
 *
 * Compliance: LEAN-01, LEAN-07 (no create from board), CT-02, CT-04, GOV-05 (existing handler).
 * PRD §12.3.6, concept doc §7.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useRorosWipStatus } from '@/hooks/roros/control-tower';
import { usePermissions } from '@/hooks/usePermissions';
import { rorosKeys } from '@/hooks/roros/keys';
import type { RorosWorkItemDTO } from '@/types/roros/control-tower';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';
import { WORK_ITEM_TRANSITIONS, getAllowedTargetColumns } from '@/types/roros/work-item-transitions';
import { WorkItemBoardColumn, ColumnConfig } from './WorkItemBoardColumn';
import { WorkItemBoardCard } from './WorkItemBoardCard';
import { MoveToExceptionsDialog, ExceptionSubStatus } from './MoveToExceptionsDialog';

// ─── Sort key type (exported for ControlTowerPage) ──────────────────────────

export type BoardSortKey = 'default' | 'priority' | 'due-asc' | 'title' | 'updated';

// ─── Column definitions ─────────────────────────────────────────────────────

/**
 * Status → CSS variable token mapping (concept §5).
 * All tokens exist in both light and dark themes.
 */
const STATUS_ACCENT: Record<string, string> = {
  backlog:              'var(--color-muted-foreground)',
  ready:                'var(--color-info-500)',
  in_progress:          'var(--color-primary-600)',
  done:                 'var(--color-neutral-500, var(--color-muted-foreground))',
  quality_confirmation: 'var(--color-warning-500)',
  released:             'var(--color-success-500)',
  exceptions:           'var(--color-error-500)',
};

const COLUMN_CONFIGS: ColumnConfig[] = [
  { id: 'backlog',              label: t('Backlog'),      statusAccent: STATUS_ACCENT.backlog,              statuses: ['backlog'] },
  { id: 'ready',                label: t('Ready'),        statusAccent: STATUS_ACCENT.ready,                statuses: ['ready'] },
  { id: 'in_progress',          label: t('In Progress'),  statusAccent: STATUS_ACCENT.in_progress,          statuses: ['in_progress'] },
  { id: 'done',                 label: t('Done'),         statusAccent: STATUS_ACCENT.done,                 statuses: ['done'] },
  { id: 'quality_confirmation', label: t('QC'),           statusAccent: STATUS_ACCENT.quality_confirmation, statuses: ['quality_confirmation'] },
  { id: 'released',             label: t('Released'),     statusAccent: STATUS_ACCENT.released,             statuses: ['released'] },
  { id: 'exceptions',           label: t('Exceptions'),   statusAccent: STATUS_ACCENT.exceptions,           statuses: ['blocked', 'escalated'] },
];

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

// ─── Sort logic (Fix 7) ─────────────────────────────────────────────────────

/**
 * Sort items for board display. Applied after the parentWorkItemId filter
 * and before column grouping, so all columns share the same ordering.
 *
 * Sort options:
 *   default  — dueDate DESC NULLS LAST, priority ASC (priority 1 = Critical = first)
 *   priority — priority ASC, dueDate DESC NULLS LAST
 *   due-asc  — dueDate ASC NULLS LAST, priority ASC
 *   title    — title ASC
 *   updated  — updatedAt DESC
 */
function sortItems(items: RorosWorkItemDTO[], sort: BoardSortKey): RorosWorkItemDTO[] {
  const sorted = [...items];
  switch (sort) {
    case 'default':
      sorted.sort((a, b) => {
        // dueDate DESC: items with a due date come first, sorted newest → oldest
        // NULLS LAST: items without a due date go to the end
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : -Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : -Infinity;
        if (aDate !== bDate) return bDate - aDate; // DESC
        // Secondary: priority ASC (1 = Critical first)
        return a.priority - b.priority;
      });
      break;

    case 'priority':
      sorted.sort((a, b) => {
        // priority ASC (1 = Critical first)
        if (a.priority !== b.priority) return a.priority - b.priority;
        // Secondary: dueDate DESC NULLS LAST
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : -Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : -Infinity;
        return bDate - aDate;
      });
      break;

    case 'due-asc':
      sorted.sort((a, b) => {
        // dueDate ASC NULLS LAST: items with a due date come first, soonest first
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        if (aDate !== bDate) return aDate - bDate; // ASC
        // Secondary: priority ASC
        return a.priority - b.priority;
      });
      break;

    case 'title':
      sorted.sort((a, b) => a.title.localeCompare(b.title));
      break;

    case 'updated':
      sorted.sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      break;
  }
  return sorted;
}

// ─── WorkItemBoard ──────────────────────────────────────────────────────────

interface WorkItemBoardProps {
  items: RorosWorkItemDTO[];
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
  onOpenDrawer: (item: RorosWorkItemDTO) => void;
  /** Active status filter from the parent page. Empty string or 'all' = no filter. */
  activeStatusFilter: string;
  /** Fix 7: Column sort key, persisted by parent in localStorage. */
  boardSort: BoardSortKey;
}

export function WorkItemBoard({
  items,
  domains,
  actors,
  onOpenDrawer,
  activeStatusFilter,
  boardSort,
}: WorkItemBoardProps) {
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const wipQuery = useRorosWipStatus();

  // Match server route guard: writeChain(PERMISSIONS.ROROS_CONTROL_TOWER_MANAGE)
  const canEdit = hasPermission('roros.control-tower.manage');

  // ── Pending moves (optimistic updates) ──
  const [pendingMoves, setPendingMoves] = useState<Map<string, string>>(new Map());
  const [activeItem, setActiveItem] = useState<RorosWorkItemDTO | null>(null);
  const [showReleasedAll, setShowReleasedAll] = useState(false);

  // ── Exceptions dialog state ──
  const [exceptionsDialogOpen, setExceptionsDialogOpen] = useState(false);
  const [pendingExceptionsMove, setPendingExceptionsMove] = useState<{
    item: RorosWorkItemDTO;
    fromStatus: string;
  } | null>(null);

  // ── Display items: root items only, with pending moves applied, then sorted (Fix 7) ──
  const displayItems = useMemo(() => {
    const rootWithPending = items
      .filter((i) => i.parentWorkItemId == null)
      .map((item) => {
        const pending = pendingMoves.get(item.id);
        return pending ? { ...item, status: pending } : item;
      });

    // Fix 7: apply column sort before grouping so all columns share the ordering
    return sortItems(rootWithPending, boardSort);
  }, [items, pendingMoves, boardSort]);

  // ── Group items into columns ──
  const columnItems = useMemo((): Record<string, RorosWorkItemDTO[]> => {
    const now = Date.now();
    const groups: Record<string, RorosWorkItemDTO[]> = {
      backlog: [], ready: [], in_progress: [], done: [],
      quality_confirmation: [], released: [], exceptions: [],
    };

    for (const item of displayItems) {
      switch (item.status) {
        case 'backlog':              groups.backlog.push(item);             break;
        case 'ready':                groups.ready.push(item);               break;
        case 'in_progress':          groups.in_progress.push(item);         break;
        case 'done':                 groups.done.push(item);                break;
        case 'quality_confirmation': groups.quality_confirmation.push(item); break;
        case 'released':
          if (showReleasedAll) {
            groups.released.push(item);
          } else {
            // Show only last 7 days by default (use updatedAt as proxy)
            const updatedMs = new Date(item.updatedAt).getTime();
            if (now - updatedMs <= SEVEN_DAYS_MS) {
              groups.released.push(item);
            }
          }
          break;
        case 'blocked':
        case 'escalated':
          groups.exceptions.push(item);
          break;
      }
    }
    return groups;
  }, [displayItems, showReleasedAll]);

  // ── Filter columns when status filter is active ──
  const visibleColumns = useMemo(() => {
    if (!activeStatusFilter || activeStatusFilter === 'all') return COLUMN_CONFIGS;
    // Map the active status filter to the column that contains it
    const matchingCol = COLUMN_CONFIGS.find((c) => c.statuses.includes(activeStatusFilter));
    return matchingCol ? [matchingCol] : COLUMN_CONFIGS;
  }, [activeStatusFilter]);

  // ── Name lookup helpers ──
  const actorName = useCallback((id: string | null): string => {
    if (!id) return '—';
    return actors.find((a) => a.id === id)?.name ?? id.slice(0, 8);
  }, [actors]);

  const domainName = useCallback((id: string): string => {
    return domains.find((d) => d.id === id)?.name ?? id.slice(0, 8);
  }, [domains]);

  // ── dnd-kit sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Drag handlers ──
  function handleDragStart(event: DragStartEvent) {
    const item = event.active.data.current?.item as RorosWorkItemDTO | undefined;
    setActiveItem(item ?? null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveItem(null);
    const { active, over } = event;
    if (!over) return;

    const draggedItem = active.data.current?.item as RorosWorkItemDTO | undefined;
    if (!draggedItem) return;

    const targetColId = over.id as string;
    const targetCol = COLUMN_CONFIGS.find((c) => c.id === targetColId);
    if (!targetCol) return;

    // Same column drop → no-op (v1 has no intra-column reordering)
    const currentColId = getColumnForStatus(draggedItem.status);
    if (currentColId === targetColId) return;

    // Validate against WORK_ITEM_TRANSITIONS
    const allowed = getAllowedTargetColumns(draggedItem.status);
    if (!allowed.includes(targetColId)) {
      // Already prevented visually; defensive guard
      return;
    }

    if (targetColId === 'exceptions') {
      // Open dialog to choose sub-status + reason
      setPendingExceptionsMove({ item: draggedItem, fromStatus: draggedItem.status });
      setExceptionsDialogOpen(true);
    } else {
      const toStatus = targetCol.statuses[0];
      performTransition(draggedItem, toStatus, null);
    }
  }

  // ── Transition logic ──
  async function performTransition(item: RorosWorkItemDTO, toStatus: string, reason: string | null) {
    // Optimistic: move card immediately
    setPendingMoves((prev) => new Map(prev).set(item.id, toStatus));

    try {
      const res = await apiFetch(`/api/v1/control-tower/work-items/${item.id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ toStatus, reason: reason ?? undefined }),
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const code = errorBody?.error?.code ?? '';
        const message = errorBody?.error?.message ?? '';
        throw { code, message };
      }

      // Success: clear optimistic state + reconcile
      setPendingMoves((prev) => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.workItems });
      qc.invalidateQueries({ queryKey: rorosKeys.controlTower.wipStatus });

    } catch (err: any) {
      // Revert optimistic move
      setPendingMoves((prev) => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });

      const code = err?.code ?? '';
      const message = err?.message ?? '';

      if (code === 'ROROS_WIP_LIMIT_REACHED') {
        showToast.error(message || t('WIP limit reached — finish or block an in-flight item first'));
      } else if (code === 'ROROS_QC_REQUIRED') {
        showToast.error(t('QC report required before releasing this item'));
      } else if (code === 'ROROS_INVALID_STATUS_TRANSITION') {
        showToast.error(t('Move not allowed — this transition is not permitted'));
      } else {
        showToast.error(message || t('Could not move work item'));
      }
    }
  }

  // ── Exceptions dialog handlers ──
  async function handleExceptionsConfirm(subStatus: ExceptionSubStatus, reason: string) {
    if (!pendingExceptionsMove) return;
    setExceptionsDialogOpen(false);
    const { item } = pendingExceptionsMove;
    setPendingExceptionsMove(null);
    await performTransition(item, subStatus, reason);
  }

  function handleExceptionsCancel() {
    setExceptionsDialogOpen(false);
    setPendingExceptionsMove(null);
  }

  // ── Allowed target columns for active drag ──
  const allowedTargetColumns = useMemo(() => {
    if (!activeItem) return new Set<string>();
    return new Set(getAllowedTargetColumns(activeItem.status));
  }, [activeItem]);

  // ── Utility: get column id for a status ──
  function getColumnForStatus(status: string): string {
    if (status === 'blocked' || status === 'escalated') return 'exceptions';
    const col = COLUMN_CONFIGS.find((c) => c.statuses.includes(status));
    return col?.id ?? 'backlog';
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/*
         * v1.1.4: h-full — board fills the flex-1 content area in ControlTowerPage.
         * The fullHeight route mode + RorosPage fullHeight prop + flex column layout
         * in ControlTowerPage propagate a defined height down to this container.
         * Horizontal scrollbar stays at the bottom of this container (= viewport bottom).
         * Columns scroll internally via WorkItemBoardColumn's overflow-y-auto.
         */}
        <div
          className={cn(
            'flex gap-3 overflow-x-auto pb-4',
            'h-full',
          )}
          tabIndex={0}
          aria-label={t('Control Tower kanban board')}
          data-testid="work-item-board"
        >
          {visibleColumns.map((col) => {
            const colItems = columnItems[col.id] ?? [];
            const isValidDrop = activeItem
              ? allowedTargetColumns.has(col.id) && getColumnForStatus(activeItem.status) !== col.id
              : false;

            return (
              <WorkItemBoardColumn
                key={col.id}
                config={col}
                items={colItems}
                wipData={wipQuery.data}
                isValidDropTarget={!activeItem || isValidDrop}
                canEdit={canEdit}
                onOpenDrawer={onOpenDrawer}
                actorName={actorName}
                domainName={domainName}
                showReleasedAll={col.id === 'released' ? showReleasedAll : undefined}
                onToggleReleasedAll={col.id === 'released' ? () => setShowReleasedAll((v) => !v) : undefined}
              />
            );
          })}
        </div>

        {/* Drag overlay — floating clone */}
        <DragOverlay>
          {activeItem && (
            <div
              className="rotate-2 scale-105"
              style={{ '--status-accent': STATUS_ACCENT[activeItem.status] ?? STATUS_ACCENT.exceptions } as React.CSSProperties}
            >
              <WorkItemBoardCard
                item={activeItem}
                isOverlay
                canEdit={false}
                onOpenDrawer={() => {}}
                actorName={actorName}
                domainName={domainName}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Exceptions move dialog */}
      <MoveToExceptionsDialog
        isOpen={exceptionsDialogOpen}
        itemTitle={pendingExceptionsMove?.item.title ?? ''}
        onConfirm={handleExceptionsConfirm}
        onCancel={handleExceptionsCancel}
      />
    </>
  );
}
